import { motion } from "framer-motion";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";

/* Shared layout for the /about, /privacy and /terms pages.
   Reuses the landing Header + Footer so the SalesPit branding, sticky nav
   and CTA band all remain consistent. Content is dropped into a max-w-3xl
   prose column with the same charcoal/red palette as the rest of the app. */

export const LegalPage = ({ eyebrow, title, subtitle, updated, children }) => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] font-body text-white">
      <Header />

      <main className="relative overflow-hidden px-6 pt-36 pb-24 lg:pt-44">
        {/* ambient red glow — same one used by Hero */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-8 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.10),transparent_60%)] blur-3xl"
        />

        <div className="relative mx-auto max-w-3xl">
          {/* Page header */}
          <div className="text-center">
            {eyebrow && (
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-[#E52525]/30 bg-[#E52525]/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF5A5A]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#E52525]" />
                {eyebrow}
              </motion.span>
            )}
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-heading mt-6 text-[2.6rem] font-black leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl"
            >
              {title}
            </motion.h1>
            {subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-5 text-lg font-light leading-relaxed text-zinc-400"
              >
                {subtitle}
              </motion.p>
            )}
            {updated && (
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-600">
                Last updated · {updated}
              </p>
            )}
          </div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-14 space-y-10"
          >
            {children}
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

/* Small helpers used across the three pages so section styling stays
   consistent. */

export const LegalSection = ({ title, children, testid }) => (
  <section data-testid={testid} className="space-y-3.5">
    <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
      {title}
    </h2>
    <div className="space-y-3 text-base leading-relaxed text-zinc-300">
      {children}
    </div>
  </section>
);

export const LegalList = ({ items }) => (
  <ul className="space-y-2.5 pl-1">
    {items.map((text, i) => (
      <li key={i} className="flex gap-3">
        <span
          aria-hidden="true"
          className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E52525]"
        />
        <span className="text-zinc-300">{text}</span>
      </li>
    ))}
  </ul>
);
